/**
 * Global Notification & Dialog System
 * ====================================
 * 
 * Unified system for all notifications and confirmation dialogs.
 * NEVER use browser's alert(), confirm(), or prompt() - ALWAYS use this!
 * 
 * Usage:
 *   import { useNotify } from '@/composables/useNotify'
 *   const { notify, confirm, alert } = useNotify()
 *   
 *   // Toast notifications
 *   notify.success('Saved successfully!')
 *   notify.error('Failed to save')
 *   notify.info('Processing...')
 *   notify.warning('Please check your input')
 *   
 *   // Confirmation dialog
 *   const confirmed = await confirm({
 *     title: 'Delete Item?',
 *     message: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     cancelText: 'Cancel',
 *     type: 'warning' // 'info' | 'warning' | 'error'
 *   })
 *   
 *   // Alert dialog (just OK button)
 *   await alert({
 *     title: 'Success',
 *     message: 'Your changes have been saved.',
 *     type: 'success'
 *   })
 */

import { ref, reactive, readonly } from 'vue'

// Global state (singleton)
const notifications = ref([])
const dialog = reactive({
  show: false,
  title: '',
  message: '',
  type: 'info',
  confirmText: 'OK',
  cancelText: 'Cancel',
  showCancel: true,
  // Prompt support (TICK-00032): optional text input inside the dialog.
  showInput: false,
  inputLabel: '',
  inputValue: '',
  inputRequired: false,
  resolve: null
})

let notificationId = 0

// Notification methods - callable in BOTH styles:
//   notify.success('msg')           (preferred)
//   notify('success', 'msg')        (legacy / safety net)
//   notify('msg')                   (= info)
const VALID_TYPES = ['success', 'error', 'info', 'warning']

function notify(typeOrMsg, msgOrTimeout, timeout) {
  if (typeof typeOrMsg === 'string' && VALID_TYPES.includes(typeOrMsg)) {
    return notify[typeOrMsg](msgOrTimeout, timeout)
  }
  return notify.info(typeOrMsg, msgOrTimeout)
}

notify.success = function (message, timeout = 4000) {
  return addNotification({ message, type: 'success', icon: 'mdi-check-circle', timeout })
}
notify.error = function (message, timeout = 6000) {
  return addNotification({ message, type: 'error', icon: 'mdi-alert-circle', timeout })
}
notify.info = function (message, timeout = 4000) {
  return addNotification({ message, type: 'info', icon: 'mdi-information', timeout })
}
notify.warning = function (message, timeout = 5000) {
  return addNotification({ message, type: 'warning', icon: 'mdi-alert', timeout })
}

/**
 * Iteration 4 (PDF 26.05.2026, pt 8.4): sticky toast WITH a click-through
 * action button. Used after Create Job to give the operator an explicit
 * "View in CRM" / "Open in Jobs tab" affordance instead of dumping them
 * back on an empty Sales Panel.
 *
 * @param {Object} opts
 * @param {string} opts.message      — toast text
 * @param {string} [opts.actionLabel] — button label (omit → no button)
 * @param {Function} [opts.onAction] — callback on action click
 * @param {string} [opts.type='success']
 * @param {string} [opts.icon='mdi-check-circle']
 * @param {number} [opts.timeoutMs=8000] — sticky enough for the user to click
 */
notify.successWithAction = function (opts) {
  const message = opts?.message || ''
  const actionLabel = opts?.actionLabel || ''
  const onAction = typeof opts?.onAction === 'function' ? opts.onAction : null
  const type = opts?.type || 'success'
  const icon = opts?.icon || (
    type === 'error' ? 'mdi-alert-circle' :
    type === 'warning' ? 'mdi-alert' :
    type === 'info' ? 'mdi-information' : 'mdi-check-circle'
  )
  const timeoutMs = typeof opts?.timeoutMs === 'number' ? opts.timeoutMs : 8000
  return addNotification({
    message, type, icon, timeout: timeoutMs,
    actionLabel, onAction,
  })
}

function addNotification({ message, type, icon, timeout, actionLabel, onAction }) {
  const id = ++notificationId
  notifications.value.push({
    id, message, type, icon, show: true,
    // Optional action button shown by GlobalNotifications.vue.
    actionLabel: actionLabel || '',
    onAction: typeof onAction === 'function' ? onAction : null,
  })

  if (timeout > 0) {
    setTimeout(() => {
      removeNotification(id)
    }, timeout)
  }

  return id
}

function removeNotification(id) {
  const index = notifications.value.findIndex(n => n.id === id)
  if (index > -1) {
    notifications.value[index].show = false
    setTimeout(() => {
      notifications.value = notifications.value.filter(n => n.id !== id)
    }, 300)
  }
}

/**
 * Show confirmation dialog
 * @param {Object} options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message
 * @param {string} options.confirmText - Confirm button text (default: 'Confirm')
 * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
 * @param {string} options.type - 'info' | 'warning' | 'error' | 'success'
 * @returns {Promise<boolean>} - true if confirmed, false if cancelled
 */
function confirm(options) {
  return new Promise((resolve) => {
    dialog.show = true
    dialog.title = options.title || 'Confirm'
    dialog.message = options.message || 'Are you sure?'
    dialog.type = options.type || 'info'
    dialog.confirmText = options.confirmText || 'Confirm'
    dialog.cancelText = options.cancelText || 'Cancel'
    dialog.showCancel = true
    dialog.showInput = false
    dialog.resolve = resolve
  })
}

/**
 * Show a prompt dialog with a required text input (global replacement for
 * browser prompt()). Resolves to the entered string, or null when cancelled.
 * @param {Object} options - title, message, label, confirmText, cancelText, type
 * @returns {Promise<string|null>}
 */
function promptDialog(options) {
  return new Promise((resolve) => {
    dialog.show = true
    dialog.title = options.title || 'Input required'
    dialog.message = options.message || ''
    dialog.type = options.type || 'info'
    dialog.confirmText = options.confirmText || 'Confirm'
    dialog.cancelText = options.cancelText || 'Cancel'
    dialog.showCancel = true
    dialog.showInput = true
    dialog.inputLabel = options.label || 'Value'
    dialog.inputValue = options.initialValue || ''
    dialog.inputRequired = options.required !== false
    dialog.resolve = (confirmed) => {
      resolve(confirmed ? dialog.inputValue : null)
    }
  })
}

/**
 * Show alert dialog (single OK button)
 * @param {Object} options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message
 * @param {string} options.confirmText - Button text (default: 'OK')
 * @param {string} options.type - 'info' | 'warning' | 'error' | 'success'
 * @returns {Promise<void>}
 */
function alert(options) {
  return new Promise((resolve) => {
    dialog.show = true
    dialog.title = options.title || 'Notice'
    dialog.message = options.message || ''
    dialog.type = options.type || 'info'
    dialog.confirmText = options.confirmText || 'OK'
    dialog.showCancel = false
    dialog.showInput = false
    dialog.resolve = () => resolve()
  })
}

function closeDialog(result) {
  dialog.show = false
  if (dialog.resolve) {
    dialog.resolve(result)
    dialog.resolve = null
  }
}

/**
 * Composable for notifications and dialogs
 */
export function useNotify() {
  return {
    // Toast notifications
    notify,
    
    // Dialogs
    confirm,
    alert,
    prompt: promptDialog,
    
    // State (for GlobalNotifications component)
    notifications: readonly(notifications),
    // NOTE: not readonly — GlobalNotifications binds v-model to dialog.inputValue.
    dialog,
    removeNotification,
    closeDialog
  }
}

// Export singleton for direct import
export { notify, confirm, alert, promptDialog, notifications, dialog, removeNotification, closeDialog }








